<template>
    <div class="zone-form-simple">
        <div class="form-row">
            <label class="form-label">Zone Name</label>
            <a-input v-model:value="zoneName" :maxLength="30" placeholder="Input a zone name"
                :disabled="props.editData?.type === 'rest_of_world'" />
            <div class="form-count">{{ zoneName.length }}/30</div>
        </div>

        <div class="form-row">
            <label class="form-label">Country(ies)</label>
            <a-button @click="openCountryModal" class="form-add-btn"
                v-if="props.editData?.type !== 'rest_of_world'">Add</a-button>
            <div v-if="selectedCountries.length > 0" class="form-list">
                <div v-for="(country, idx) in selectedCountries" :key="country.iso2" class="form-list-item">
                    <span v-html="YxCountry.flag(country.iso2)" class="w-5 h-3 inline-block"></span>
                    <span class="form-list-name">{{ country.name }}</span>
                    <a-button type="link" @click="removeCountry(idx)" class="form-remove-btn"
                        v-if="props.editData?.type !== 'rest_of_world'">×</a-button>
                </div>
            </div>
            <div v-else class="form-empty">No countries selected</div>
        </div>

        <div class="form-row">
            <label class="form-label">Funnel(s)</label>
            <div style="display: flex; align-items: center; justify-content: flex-end; gap: 10px; margin-bottom: 8px;">
                <a-select v-model:value="splitType" style="width: 160px;">
                    <a-select-option value="even">Split evenly</a-select-option>
                    <a-select-option value="customized">Split by weight</a-select-option>
                </a-select>
                <a-button @click="openFunnelModal" class="form-add-btn">Add</a-button>
            </div>
            <div v-if="selectedFunnels.length > 0" class="form-list">
                <div v-for="(funnel, idx) in selectedFunnels" :key="funnel.id" class="form-list-item">
                    <span class="form-list-name">{{ funnel.name }}</span>
                    <div class="form-slider">
                        <a-slider v-model:value="funnel.weight" :min="0" :max="100" :step="1"
                            :disabled="splitType === 'even' || (idx === selectedFunnels.length - 1 && splitType === 'customized')"
                            style="width: 120px;" @change="handleFunnelWeightChange(funnel)" />
                        <span class="form-slider-value">{{ funnel.weight }} %</span>
                    </div>
                    <a-button type="link" @click="removeFunnel(idx)" class="form-remove-btn">×</a-button>
                </div>
            </div>
            <div v-else class="form-empty">No funnels selected</div>
        </div>
    </div>

    <a-modal v-model:open="showCountryModal" title="Select Countries" :maskClosable="false" @cancel="closeCountryModal"
        @ok="handleCountrySelect" width="600px">
        <ZonesCountry ref="zonesCountryRef" :forbiddenCountries="forbiddenCountries" />
    </a-modal>
    <a-modal v-model:open="showFunnelModal" title="Select Funnels" :maskClosable="false" @cancel="closeFunnelModal"
        @ok="handleFunnelSelect" width="580px">
        <Funnels ref="funnelRef" />
    </a-modal>
</template>

<script setup lang="ts">
import { ref, nextTick, watch, computed } from 'vue';
import { message } from 'ant-design-vue';
import ZonesCountry from '@/components/zone/Countries.vue';
import Funnels from '@/components/zone/Funnels.vue';
import { createZone, updateZone } from '@/api/zones';
import YxCountry from '@/data/countries';
import { useZoneStore } from '@/store/modules/zone';

interface Country {
    iso2: string;
    name: string;
    emoji?: string;
}

interface Funnel {
    id: number;
    name: string;
    status: string;
    weight: number;
}


const props = defineProps<{
    editData?: {
        id: number;
        name: string;
        countries: string;
        funnels: any[];
        distributionType: 'even' | 'customized';
        type: string;
    }
}>();

const emit = defineEmits<{
    (e: 'success'): void;
    (e: 'cancel'): void;
}>();

const zoneStore = useZoneStore()

const forbiddenCountries = computed(() => zoneStore.getForbiddenCountries)

// State
const zonesCountryRef = ref<InstanceType<typeof ZonesCountry>>();
const funnelRef = ref<InstanceType<typeof Funnels>>();
const showCountryModal = ref(false);
const showFunnelModal = ref(false);
const loading = ref(false);
const zoneName = ref('');
const selectedCountries = ref<Country[]>([]);
const selectedFunnels = ref<Funnel[]>([]);
const splitType = ref<'even' | 'customized'>('even');

watch(splitType, (val) => {
    if (val === 'even') {
        evenDistributeFunnels();
    }
});

function evenDistributeFunnels() {
    if (selectedFunnels.value.length === 0) return;
    const avg = Math.floor(100 / selectedFunnels.value.length);
    const remainder = 100 - avg * selectedFunnels.value.length;
    selectedFunnels.value.forEach((f, idx) => {
        f.weight = avg + (idx === 0 ? remainder : 0);
    });
}

// Methods
const initForm = () => {
    if (!props.editData) return;

    zoneName.value = props.editData.name;
    splitType.value = props.editData.distributionType;

    // Set selected countries
    if (Array.isArray(props.editData.countries)) {
        selectedCountries.value = props.editData.countries.map(code => ({
            iso2: code,
            name: zoneStore.getCountryNameByIso2(code),
            emoji: YxCountry.flag(code)
        }));
    } else if (typeof props.editData.countries === 'string') {
        const countryCodes = props.editData.countries.split(',');
        selectedCountries.value = countryCodes.map(code => ({
            iso2: code,
            name: zoneStore.getCountryNameByIso2(code),
            emoji: YxCountry.flag(code)
        }));
    }

    // Set selected funnels
    if (Array.isArray(props.editData.funnels)) {
        selectedFunnels.value = props.editData.funnels.map(funnel => ({
            id: funnel.id,
            name: funnel.name,
            status: 'active',
            weight: funnel.weight || 0
        }));
    }
};

const resetForm = () => {
    zoneName.value = '';
    selectedCountries.value = [];
    selectedFunnels.value = [];
    splitType.value = 'even';
    loading.value = false;
};

const openCountryModal = () => {
    showCountryModal.value = true;
    nextTick(() => {
        if (zonesCountryRef.value) {
            const selected = selectedCountries.value.map(country => country.iso2);
            zonesCountryRef.value.initSelectedCountries(selected);
        }
    });
};

const closeCountryModal = () => {
    showCountryModal.value = false;
};

const handleCountrySelect = () => {
    selectedCountries.value = zonesCountryRef.value?.getSelectedCountries() || [];
    closeCountryModal();
};

const removeCountry = (idx: number) => {
    selectedCountries.value.splice(idx, 1);
};

const openFunnelModal = () => {
    showFunnelModal.value = true;
    nextTick(() => {
        if (funnelRef.value) {
            funnelRef.value.initSelectedFunnels(selectedFunnels.value);
        }
    });
};

const closeFunnelModal = () => {
    showFunnelModal.value = false;
};

const handleFunnelSelect = () => {
    selectedFunnels.value = funnelRef.value?.getSelectedFunnels() || [];
    closeFunnelModal();
    if (splitType.value === 'even') {
        evenDistributeFunnels();
    }
};

const handleFunnelWeightChange = (funnel: Funnel) => {
    if (splitType.value === 'even') return;
    const lastFunnelIndex = selectedFunnels.value.length - 1;
    const lastFunnel = selectedFunnels.value[lastFunnelIndex];
    if (funnel.id === lastFunnel.id) {
        return;
    }
    const otherFunnelsTotal = selectedFunnels.value
        .filter(f => f.id !== funnel.id && f.id !== lastFunnel.id)
        .reduce((sum, f) => sum + f.weight, 0);
    if (funnel.weight + otherFunnelsTotal > 100) {
        funnel.weight = 100 - otherFunnelsTotal;
    }
    lastFunnel.weight = Math.max(0, 100 - (otherFunnelsTotal + funnel.weight));
};

const removeFunnel = (idx: number) => {
    selectedFunnels.value.splice(idx, 1);
    // 删除后重新计算概率
    if (splitType.value === 'even') {
        evenDistributeFunnels();
    } else if (splitType.value === 'customized' && selectedFunnels.value.length > 0) {
        // 重新计算最后一个漏斗的权重，确保总和为100%
        const lastFunnelIndex = selectedFunnels.value.length - 1;
        const otherFunnelsTotal = selectedFunnels.value
            .slice(0, lastFunnelIndex)
            .reduce((sum, f) => sum + f.weight, 0);
        selectedFunnels.value[lastFunnelIndex].weight = Math.max(0, 100 - otherFunnelsTotal);
    }
};

const submitForm = async () => {
    if (!zoneName.value.trim()) {
        message.error('Please enter a zone name');
        return;
    }
    loading.value = true;

    if (zoneName.value.length > 30 || zoneName.value.length < 3) {
        message.error('Zone name must be between 3 and 30 characters');
        return;
    }

    if (selectedCountries.value.length === 0) {
        message.error('Please select at least one country');
        return;
    }

    try {
        if (props.editData) {
            await updateZone({
                zone: {
                    id: props.editData.id,
                    name: zoneName.value,
                    type: props.editData.type,
                    distributionType: splitType.value,
                    funnels: selectedFunnels.value.map(funnel => ({ id: funnel.id, weight: funnel.weight })),
                    countries: selectedCountries.value.map(country => country.iso2).join(','),
                }
            });
            message.success('Zone updated successfully');
        } else {
            await createZone({
                zone: {
                    name: zoneName.value,
                    type: 'country',
                    distributionType: splitType.value,
                    funnels: selectedFunnels.value.map(funnel => ({ id: funnel.id, weight: funnel.weight })),
                    countries: selectedCountries.value.map(country => country.iso2).join(','),
                }
            });
            message.success('Zone created successfully');
        }
        emit('success');
    } catch (error) {
        console.error('Error creating/updating zone:', error);
        message.error('Failed to create/update zone');
    } finally {
        loading.value = false;
    }
};

// Expose methods to parent
defineExpose({
    initForm,
    resetForm,
    submitForm,
    loading
});
</script>

<style scoped>
.zone-form-simple {
    padding: 8px 0;
}

.form-row {
    margin-bottom: 22px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-label {
    font-weight: 500;
    margin-bottom: 2px;
    color: #222;
}

.form-count {
    text-align: right;
    color: #aaa;
    font-size: 12px;
}

.form-add-btn {
    margin-bottom: 0;
    align-self: flex-end;
}

.form-list {
    background: #fafafa;
    border-radius: 6px;
    padding: 6px 12px;
    max-height: 300px;
    overflow-y: auto;
}

.form-list-item {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    border-bottom: 1px solid #efefef;
    padding: 2px 0;
}

.form-list-item:last-child {
    border-bottom: none;
}

.form-list-name {
    flex: 1;
    margin-left: 8px;
}

.form-remove-btn {
    color: #ff0000;
    margin-left: 8px;
}

.form-empty {
    text-align: center;
    color: #999;
    font-size: 14px;
    background: #fafafa;
    border-radius: 6px;
    height: 60px;
    line-height: 60px;
    margin-top: 8px;
}

.form-slider {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
}

.form-slider-value {
    color: #666;
    min-width: 60px;
}
</style>
